// Special throttle hook that returns the latest arguments when the function is called
import { useEffect, useRef } from 'react';

type ThrottledFunction<T extends (...args: any[]) => void> = (
  ...args: Parameters<T>
) => void;

function useThrottle<T extends (...args: any[]) => void>(
  func: T,
  delay: number
): ThrottledFunction<T> {
  const lastExecutedTime = useRef<number>(0);
  const lastArgs = useRef<Parameters<T>>(null as any); // Use `null` as a placeholder initially
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const throttledFunction = (...args: Parameters<T>) => {
    const currentTime = Date.now();
    lastArgs.current = args; // Store the most recent arguments

    // If enough time has passed, invoke the function
    if (currentTime - lastExecutedTime.current >= delay) {
      func(...args); // Call the function with the most recent args
      lastExecutedTime.current = currentTime;
    } else if (!timeoutRef.current) {
      // If there's no active timeout, set one
      timeoutRef.current = setTimeout(() => {
        func(...(lastArgs.current || [])); // Ensure we call it with the latest args
        lastExecutedTime.current = Date.now();
        timeoutRef.current = null;
      }, delay - (currentTime - lastExecutedTime.current));
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current); // Clean up the timeout on unmount
      }
    };
  }, []);

  return throttledFunction;
}

export default useThrottle;
